import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import MobileSubNavigator from '../../components/MobileSubNavigator';
import { getHomeLinks } from '../../utils/homeLinks';
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { tokens, Badge, Button } from '../../components/admin/AdminUI';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const c = tokens.color;
const TARGET_LABEL = 'biz.antsnest.co.kr';
const PROBE_PATH = '/';
const INTERVAL_MS = 3000;
const MAX_POINTS = 30;

const Container = styled.div`
  width: 100%; padding: 1rem; background: ${c.appBg}; min-height: 100vh;
  box-sizing: border-box; font-family: ${tokens.font}; color: ${c.text};
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.6rem; margin-bottom: 1rem;
`;
const SCard = styled.div`
  background: ${c.surface}; border: 1px solid ${c.border}; border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.xs}; padding: 14px 16px; display: flex; flex-direction: column; gap: 4px;
`;
const SLabel = styled.span` font-size: 12px; color: ${c.muted}; `;
const SValue = styled.span` font-size: 19px; font-weight: 700; color: ${(p) => p.color || c.heading}; `;
const SHint = styled.span` font-size: 11px; color: ${c.subtle}; `;
const ChartCard = styled.div`
  background: ${c.surface}; border: 1px solid ${c.border}; border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card}; padding: 16px; margin-bottom: 1rem;
`;
const ChartHead = styled.div` display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; flex-wrap: wrap; `;
const ChartTitle = styled.div` font-size: 14px; font-weight: 700; color: ${c.heading}; `;
const ChartWrap = styled.div` height: 200px; `;

function fmt(ms) { return ms == null ? '—' : `${Math.round(ms)} ms`; }
function fmtRate(kbs) { return kbs == null ? '—' : kbs >= 1024 ? `${(kbs / 1024).toFixed(1)} MB/s` : `${kbs.toFixed(0)} KB/s`; }

export default function MobileStatistic() {
  const links = getHomeLinks();
  const [samples, setSamples] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [sys, setSys] = useState(null);
  const [running, setRunning] = useState(true);
  const [historyRange, setHistoryRange] = useState('hour');
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);
  const lastTraffic = useRef(null);

  const probe = useCallback(async () => {
    const start = performance.now();
    let ok = false, ms = null;
    try {
      await fetch(`${PROBE_PATH}?_p=${start}`, { method: 'HEAD', cache: 'no-store', credentials: 'include' });
      ms = performance.now() - start; ok = true;
    } catch (e) { ok = false; ms = null; }
    try {
      const r = await fetch(`/app-traffic?_t=${start}`, { cache: 'no-store', credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (d.process || d.dbPool) setSys({ process: d.process, dbPool: d.dbPool });
        const prev = lastTraffic.current;
        if (prev) {
          const dt = Math.max(0.001, (d.now - prev.now) / 1000);
          const inKBs = Math.max(0, (d.inBytes - prev.inBytes) / 1024 / dt);
          const outKBs = Math.max(0, (d.outBytes - prev.outBytes) / 1024 / dt);
          setTraffic((a) => [...a, { t: new Date(), inKBs, outKBs }].slice(-MAX_POINTS));
        }
        lastTraffic.current = d;
      }
    } catch (e) { /* ignore */ }
    setSamples((prev) => [...prev, { t: new Date(), ms, ok }].slice(-MAX_POINTS));
  }, []);

  useEffect(() => {
    if (!running) return;
    probe();
    timerRef.current = setInterval(probe, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [running, probe]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`/app-metrics-history?range=${historyRange}`, { cache: 'no-store', credentials: 'include' });
        if (r.ok) { const d = await r.json(); if (alive) setHistory(d.points || []); }
      } catch (e) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [historyRange]);

  const okS = samples.filter((s) => s.ok && s.ms != null);
  const latest = samples[samples.length - 1];
  const current = latest && latest.ok ? latest.ms : null;
  const avg = okS.length ? okS.reduce((a, s) => a + s.ms, 0) / okS.length : null;
  const successRate = samples.length ? Math.round((samples.filter((s) => s.ok).length / samples.length) * 100) : null;
  const online = latest ? latest.ok : null;
  const lt = traffic[traffic.length - 1];

  const grade = current == null ? { tone: 'neutral', text: '측정 중' }
    : current < 150 ? { tone: 'success', text: '빠름' }
    : current < 500 ? { tone: 'warning', text: '보통' } : { tone: 'danger', text: '느림' };

  const lineBase = (label, data, color) => ({
    label, data, borderColor: color, backgroundColor: 'transparent',
    borderWidth: 2, pointRadius: 1, tension: 0.35,
  });
  const latencyData = { labels: samples.map((s) => ''), datasets: [{ ...lineBase('ms', samples.map((s) => (s.ok ? Math.round(s.ms) : null)), c.primary), backgroundColor: 'rgba(13,110,253,0.08)', fill: true, spanGaps: true }] };
  const baseOpt = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 6, color: c.muted } }, y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 9 }, color: c.muted } } } };

  const histLabel = (ts) => { const d = new Date(ts); return historyRange === 'hour' ? `${String(d.getHours()).padStart(2, '0')}:00` : `${d.getMonth() + 1}/${d.getDate()}`; };
  const historyData = {
    labels: history.map((p) => histLabel(p.ts)),
    datasets: [
      { ...lineBase('CPU %', history.map((p) => p.cpu), c.primary), yAxisID: 'y', backgroundColor: 'rgba(13,110,253,0.08)', fill: true },
      { ...lineBase('RAM MB', history.map((p) => p.rssMB), c.teal), yAxisID: 'y1' },
    ],
  };
  const histOpt = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top', labels: { font: { size: 10 }, color: c.muted, boxWidth: 10 } } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 6, color: c.muted } },
      y: { position: 'left', beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 9 }, color: c.muted, callback: (v) => `${v}%` } },
      y1: { position: 'right', beginAtZero: true, grid: { display: false }, ticks: { font: { size: 9 }, color: c.muted, callback: (v) => `${v}M` } } } };

  const RANGES = [{ k: 'hour', label: '시간별' }, { k: 'day', label: '일별' }, { k: 'week', label: '주간' }];

  return (
    <MobileSubNavigator links={links} sectionTitle="대시보드" backPath="/" showBackButton={true}>
      <Container>
        <Grid>
          <SCard><SLabel>상태</SLabel><SValue color={online == null ? undefined : online ? c.successText : c.dangerText}>{online == null ? '측정 중' : online ? '정상' : '응답없음'}</SValue><SHint>{TARGET_LABEL}</SHint></SCard>
          <SCard><SLabel>현재 응답</SLabel><SValue color={c.primary}>{fmt(current)}</SValue><SHint>평균 {fmt(avg)}</SHint></SCard>
          <SCard><SLabel>성공률</SLabel><SValue color={successRate === 100 ? c.successText : undefined}>{successRate == null ? '—' : `${successRate}%`}</SValue><SHint>최근 {samples.length}회</SHint></SCard>
          <SCard><SLabel>트래픽 ↓/↑</SLabel><SValue style={{ fontSize: 15 }}>{fmtRate(lt ? lt.inKBs : null)}</SValue><SHint>↑ {fmtRate(lt ? lt.outKBs : null)}</SHint></SCard>
          <SCard><SLabel>프로세스 CPU</SLabel><SValue color={c.primary}>{sys?.process ? `${sys.process.cpuPercent.toFixed(0)}%` : '—'}</SValue><SHint>RAM {sys?.process ? `${Math.round(sys.process.rssMB)}MB` : '—'}</SHint></SCard>
          <SCard><SLabel>DB 풀</SLabel><SValue>{sys?.dbPool ? `${sys.dbPool.active}/${sys.dbPool.max}` : '—'}</SValue><SHint>대기 {sys?.dbPool ? sys.dbPool.waiting : '—'}</SHint></SCard>
        </Grid>

        <ChartCard>
          <ChartHead>
            <ChartTitle>응답시간</ChartTitle>
            <Badge $tone={grade.tone} $dot>{grade.text}</Badge>
          </ChartHead>
          <ChartWrap><Line data={latencyData} options={baseOpt} /></ChartWrap>
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <Button $variant={running ? 'ghost' : 'primary'} $size="sm" onClick={() => setRunning((r) => !r)}>{running ? '일시정지' : '재시작'}</Button>
          </div>
        </ChartCard>

        <ChartCard>
          <ChartHead>
            <ChartTitle>사용량 이력</ChartTitle>
            <div style={{ display: 'flex', gap: 4 }}>
              {RANGES.map((r) => (
                <Button key={r.k} $size="sm" $variant={historyRange === r.k ? 'primary' : 'ghost'} onClick={() => setHistoryRange(r.k)}>{r.label}</Button>
              ))}
            </div>
          </ChartHead>
          <ChartWrap>
            {history.length === 0 ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.subtle, fontSize: 12 }}>이력 수집 중 (5분 간격)</div>
            ) : (
              <Line data={historyData} options={histOpt} />
            )}
          </ChartWrap>
        </ChartCard>
      </Container>
    </MobileSubNavigator>
  );
}
